/**
 * The rundown editor's piece types: the SHOW STYLE's (D22, D32) — the piece
 * types, their param specs and source layers the rundown's pieces and adlibs
 * will play with — no longer a static registry. The piece form is built from
 * a type's param specs (`components/editor/PieceForm.tsx`, over the operator
 * screen's own param form, `lib/playout/paramForm.ts`), and checked with the
 * engine's `checkParams`, as the production will check it.
 *
 * **Which show style.** A rundown is not bound to a show style; a production
 * picks one when it is made (its own, else its studio's default). So the
 * editor takes, in order (`resolveEditorShowStyle`):
 *
 *   production  the show style a production running this rundown (or its playlist) runs — what will play;
 *               nothing overrides it while it runs
 *   chosen      the one picked on this page ("Piece types from", kept in this browser per rundown; "Automatic" clears it)
 *   studio      the default show style of the studio the rundown is bound to (`studioId`)
 *   only        the tenant's only show style
 *   none        nothing applies: the form says so and offers the picker; a piece's params show raw
 *
 * A piece row stores its type in `f.pieceType` and its parameters as leaves
 * of the open `params` namespace (`params.input`, …: one LWW register per
 * leaf, so two people editing two parameters both win — `fields.ts`).
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import { checkParams } from '@acp/engine/params.js';
import type { PieceTypeConfig } from '@acp/engine/productionConfig.js';
import { parseShowStyleConfig } from '@acp/engine/showStyle.js';

export type EditorPieceType = {
  readonly id: string;
  readonly label: string;
  /** Written to `sourceLayer` when a piece takes this type. */
  readonly sourceLayer: string;
  /** The type as the engine runs it: its param specs, for the form and `checkParams`. */
  readonly config: PieceTypeConfig;
};

/** A show style config's piece types, in its order; none when it does not parse (the form then shows raw params). */
export function editorPieceTypes(showStyleConfig: unknown): EditorPieceType[] {
  const parsed = parseShowStyleConfig(showStyleConfig);
  if (!parsed.ok) return [];
  return Object.entries(parsed.config.pieceTypes).map(([id, config]) => ({ id, label: config.label === '' ? id : config.label, sourceLayer: config.sourceLayer, config }));
}

/** The tag the tree shows for a piece: its type id (what the show style calls it), else a dash. */
export function pieceTypeShort(id: string): string {
  return id === '' ? '—' : id;
}

export type ShowStyleSource = 'chosen' | 'production' | 'studio' | 'only' | 'none';

export type EditorShowStyle = { readonly showStyleId: string | null; readonly source: ShowStyleSource };

export type ShowStyleFacts = {
  /** Picked on this page; ignored unless it is one of `showStyleIds`. */
  readonly chosen: string | null;
  /** The productions running this rundown (or its playlist) now. */
  readonly running: readonly { readonly studioId: string; readonly showStyleId: string | null }[];
  /** The rundown's own studio binding (`f.studioId`). */
  readonly rundownStudioId: string | null;
  readonly studios: readonly { readonly studioId: string; readonly defaultShowStyleId: string | null }[];
  /** The tenant's show styles (the picker's list). */
  readonly showStyleIds: readonly string[];
};

/** Which show style's piece types the editor offers (the module comment's order). */
export function resolveEditorShowStyle(facts: ShowStyleFacts): EditorShowStyle {
  const defaultOf = (studioId: string | null): string | null => (studioId === null ? null : (facts.studios.find((s) => s.studioId === studioId)?.defaultShowStyleId ?? null));
  for (const production of facts.running) {
    const id = production.showStyleId ?? defaultOf(production.studioId);
    if (id !== null) return { showStyleId: id, source: 'production' };
  }
  if (facts.chosen !== null && facts.showStyleIds.includes(facts.chosen)) return { showStyleId: facts.chosen, source: 'chosen' };
  const bound = defaultOf(facts.rundownStudioId);
  if (bound !== null) return { showStyleId: bound, source: 'studio' };
  const [only, ...more] = facts.showStyleIds;
  if (only !== undefined && more.length === 0) return { showStyleId: only, source: 'only' };
  return { showStyleId: null, source: 'none' };
}

/** Where the editor keeps this browser's pick for a rundown. */
export function chosenShowStyleKey(rundownId: string): string {
  return `acp.editor.showStyle.${rundownId}`;
}

/**
 * Why the production would refuse a piece of `type` with `params` (the
 * engine's `checkParams`: a required param missing, a value off its spec), or
 * null when it would play. Shown under the piece form.
 */
export function pieceParamsProblem(type: EditorPieceType, params: Readonly<Record<string, JsonValue>>): string | null {
  const checked = checkParams(type.config, params);
  return checked.ok ? null : checked.error;
}

/** A piece's params beside its type's specs: those the type does not name (kept on the row as they are, PLAN §6.8). */
export function unnamedParams(type: EditorPieceType, params: Readonly<Record<string, JsonValue>>): Record<string, JsonValue> {
  return Object.fromEntries(Object.entries(params).filter(([key]) => !Object.hasOwn(type.config.params, key)));
}
